import * as angular from 'angular';
import * as moment from 'moment';

import { ComponentDefinition, Identifier, IdentifierType, IValueViewModel } from '../../models';
import { infoTextColor } from '../../common/constants';
import { IidentifierService, IDateRangeService, IGroupService, IReferenceStateService, IwidgetIntervalHelperService } from '../../services';
import { refreshClick } from '../../common/events';
import { ChartDirectiveConfiguration } from '../../framework/angular/directives/charts/chartDirectiveConfiguration';
import { IPaneConfiguration } from '../../framework/charting/interfaces/IPaneConfiguration';

class IdentifierTrendsController implements angular.IComponentController {
    groupGuid: string;
    isDataLoading: boolean;
    isLoading: boolean;
    loadingMessage: string;
    selectedIdentifier: IValueViewModel;
    trend: ChartDirectiveConfiguration;

    private _garbages: (() => void)[] = [];

    private _referenceStatesPane = {
        height: 8,
        isXaxisVisible: false,
        isYaxisVisible: false,
        isLegendVisible: false,
        yMin: 0,
        yMax: 1,
        areaClickCallback: this.stateClickCallback(),
        title: this._$translate.instant('Reference states'),
        isTitleVisible: true,
        editMode: true,
        isTitleLeftAligned: true
    };

    private _tagPanes: IPaneConfiguration[] = [
        {
            isXaxisVisible: false,
            isYaxisVisible: true,
            height: 30
        },
        {
            isXaxisVisible: false,
            isYaxisVisible: true,
            height: 30
        },
        {
            isXaxisVisible: true,
            isYaxisVisible: true,
            height: 32
        },
        this._referenceStatesPane
    ];

    private _calculatedPanes: IPaneConfiguration[] = [
        {
            isXaxisVisible: false,
            height: 45
        },
        {
            isXaxisVisible: true,
            height: 47
        },
        this._referenceStatesPane
    ];

    static $inject = [
        '$scope',
        '$translate',
        '$stateParams',
        'identifierService',
        'dateRangeService',
        'groupService',
        'referenceStateService',
        'widgetIntervalHelperService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$translate: angular.translate.ITranslateService,
        private _$stateParam: angular.ui.IStateParamsService,
        private _identifierService: IidentifierService,
        private _dateRangeService: IDateRangeService,
        private _groupService: IGroupService,
        private _referenceStateService: IReferenceStateService,
        private _widgetIntervalHelperService: IwidgetIntervalHelperService) {
    }

    $onInit() {
        const series = [
            {
                name: 'Average',
                value: d => d.average,
                selector: d => d.guid,
                identifier: 0,
                color: infoTextColor,
                pane: 0,
                isLineVisible: true
            }, {
                name: 'Standard deviation',
                value: d => d.standardDeviation,
                selector: d => d.guid,
                identifier: 0,
                color: '#c36',
                pane: 1,
                isLineVisible: true
            }, {
                name: 'Gradient',
                value: d => d.gradient,
                selector: d => d.guid,
                identifier: 0,
                color: '#990',
                pane: 2,
                isLineVisible: true
            }
        ];
        const trend = {
            options: {
                isTitleVisible: false,
                isLegendVisible: false,
                panes: this._tagPanes,
                display: {
                },
                title: null,
                isHorizontalGridlinesVisible: false,
                isVerticalGridlinesVisible: false,
                xAxis: null,
                yAxis: null,
                horizontalLines: null,
                hasAbsolutePanesHeight: false,
                crosshairDate: null,
                showXAxis: true,
                showYAxis: true,
            },
            series: series,
            data: []
        };
        this.trend = trend;
        this.groupGuid = (<any>this._$stateParam).groupGuid;

        this._garbages.push(this._$scope.$watch(() => this.selectedIdentifier, () => {
            this.refresh();
        }));

        this._garbages.push(this._$scope.$on(refreshClick, () => {
            this.refresh();
        }));
    }

    $onDestroy() {
        this._garbages.forEach(g => g());
    }
    
    refresh() {
        this.isLoading = true;
        if (this._dateRangeService.initialized() && this.selectedIdentifier) {
            const options = this.trend.options;
            options.display = {
                xMin: moment(this._dateRangeService.beginDate() as string).toDate(),
                xMax: moment(this._dateRangeService.endDate() as string).toDate()
            };

            this._identifierService
                .getNativeIdentifierByGuid(this.selectedIdentifier.guid)
                .then(identifier => {
                    this.clearReferenceStates();
                    if (identifier.type === IdentifierType.Tag) {
                        options.panes = this._tagPanes;
                    } else if (identifier.type === IdentifierType.CalculatedValue) {
                        options.panes = this._calculatedPanes;
                    }
                    const updatedInterval = this._widgetIntervalHelperService.getInterval(this._dateRangeService.beginDate() as string, this._dateRangeService.endDate() as string,
                        {
                            isEndDateIncluded: false,
                            maxPeriod: undefined,
                            minPeriod: undefined
                        });
                    this._identifierService
                        .getIdentifierValues(identifier.guid, updatedInterval.beginMoment.toISOString(), updatedInterval.endMoment.toISOString())
                        .then(data => {
                            const series = this.getSeries(identifier);
                            this.trend = {
                                options: options,
                                series: series,
                                data: data
                            };
                        })
                        .catch(reason => {
                            console.log(reason);
                        })
                        .finally(() => {
                            this.isLoading = false;
                            this.refreshReferenceStates();
                        });
                });
        }
    }

    private clearReferenceStates(): void {
        this.trend.options.panes.forEach(p => {
            p.areas = [];
        });
    }

    private refreshReferenceStates(): angular.IPromise<any> {
        return this._groupService.getReferenceStates(this.groupGuid)
            .then(responses => {
                this.trend.options.panes[this.trend.options.panes.length - 1].areas =
                    responses.map(this._referenceStateService.getReferenceStateAreaConfiguration);
            });
    }

    private getSeries(identifier: Identifier): any[] {
        const series: any[] = [];
        // passed identifier should be native
        const guid = identifier.guid;
        let pane = 0;
        const average = {
            name: 'Average',
            value: d => d.average,
            selector: d => d.guid,
            identifier: guid,
            color: infoTextColor,
            pane: pane,
            isLineVisible: true
        };
        series.push(average);
        pane++;
        if (identifier.type === IdentifierType.Tag) {
            const stdDev = {
                name: 'Standard deviation',
                value: d => d.standardDeviation,
                selector: d => d.guid,
                identifier: guid,
                color: '#c36',
                pane: pane,
                isLineVisible: true
            };
            series.push(stdDev);
            pane++;
        }
        const gradients = {
            name: 'Gradient',
            value: d => d.gradient,
            selector: d => d.guid,
            identifier: guid,
            color: '#990',
            pane: pane,
            isLineVisible: true
        };
        series.push(gradients);
        return series;
    }

    private stateClickCallback() {
        const self = this;
        return (d: any) => {
            this.trend.options.panes[this.trend.options.panes.length - 1].areas.forEach(s => s.opacity = 1);
            for (let i = 0; i < self.trend.options.panes.length - 1; i++) {
                self.trend.options.panes[i].areas = [d];
            }
            d.opacity = 0.5;
            self._$scope.$apply();
        };
    }
}

export const wtIdentifierTrends: ComponentDefinition = {
    componentId: 'wtIdentifierTrends',
    controller: IdentifierTrendsController,
    template: require('./identifierTrends.component.html'),
    controllerAs: 'vm',
    bindings: {
        item: '=',
        selectedIdentifier: '='
    }
};
